import { useState } from "react";
import {
  CheckCircle2,
  ChevronUp,
  Circle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Info,
  MessageCircle,
  Moon,
  Paperclip,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn, formatRelative } from "@/lib/utils";
import { useFetchSignedAssetUrl } from "@/hooks/use-signed-asset-url";
import { useSetProgressStatus } from "./use-set-progress-status";
import { useUnsnoozeTask } from "./use-unsnooze-task";
import { TaskSnoozeModal } from "./task-snooze-modal";
import { TaskProofInput } from "./task-proof-input";
import { TaskCommentThread } from "./task-comment-thread";
import { JourneyTaskFormDrawer } from "./journey-task-form-drawer";
import type { PortalJourneyProgressWithTask } from "./types";

// Single-task card. The most-reused component in this sub-bucket: composed
// at top-level inside the journey detail page's flat task list.
//
// Discriminates on task.action.kind:
//   - `none`            → plain checkbox toggle
//   - `note`/`link`/
//     `attachment`       → embed task-proof-input (provides the kind-specific
//                          input + a "Record & complete" CTA)
//   - `form_submission` → "Open form" button → drawer with FormRenderer
//   - `info_note`/
//     `info_link`/
//     `info_attachment`  → admin-authored content rendered inline; partner
//                          marks complete via the same checkbox toggle as
//                          `none` (no proof payload)
//
// Always available: snooze (unless completed), unsnooze (if snoozed),
// comments expander (lazy-mounted via task-comment-thread), uncomplete
// (if completed).
//
// Mobile-first: the affordances collapse into a 3-button row under the
// title on narrow viewports.

type Props = {
  progress: PortalJourneyProgressWithTask;
  assignmentId: string;
};

export function TaskCard({ progress, assignmentId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const setStatus = useSetProgressStatus();
  const unsnooze = useUnsnoozeTask();

  const action = progress.task.action;
  const isComplete = progress.status === "completed";
  const isSnoozed = progress.status === "snoozed";
  const isOwnerInternal = progress.task.assignee_kind !== "partner";
  // Sub-bucket 14: admin-content kinds complete via simple status flip,
  // same path as `none` (no proof payload).
  const isSimpleToggle =
    action.kind === "none" ||
    action.kind === "info_note" ||
    action.kind === "info_link" ||
    action.kind === "info_attachment";

  function handleSimpleToggle(next: boolean) {
    setStatus.mutate({
      progressId: progress.id,
      assignmentId,
      status: next ? "completed" : "pending",
    });
  }

  function handleUnsnooze() {
    unsnooze.mutate({ progressId: progress.id, assignmentId });
  }

  const inlineAffordance =
    !isComplete && !isSnoozed && action.kind === "form_submission";

  const expandable =
    action.kind === "note" ||
    action.kind === "link" ||
    action.kind === "attachment" ||
    showComments ||
    expanded;

  return (
    <li
      className={cn(
        "rounded-xl border bg-card transition-colors",
        isComplete ? "border-status-active/20 bg-status-active/5" : "",
        isSnoozed ? "border-amber-200/60 bg-amber-50/30 dark:bg-amber-950/10" : "",
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="pt-0.5">
          {isSimpleToggle ? (
            <Checkbox
              checked={isComplete}
              disabled={setStatus.isPending || isSnoozed}
              onCheckedChange={(v) => handleSimpleToggle(Boolean(v))}
              aria-label={`Toggle ${progress.task.title}`}
            />
          ) : isComplete ? (
            <CheckCircle2 className="h-5 w-5 text-status-active" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className={cn(
                "font-display text-sm font-semibold tracking-tight",
                isComplete ? "text-muted-foreground line-through" : "",
              )}
            >
              {progress.task.title}
            </h3>
            <StatusBadge progress={progress} />
            {isOwnerInternal ? (
              <Badge variant="secondary" className="font-normal text-[10px]">
                Bookboost
              </Badge>
            ) : null}
            <ProofKindBadge action={action} isComplete={isComplete} />
          </div>

          {progress.task.description ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {progress.task.description}
            </p>
          ) : null}

          <InfoActionBody action={action} />

          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            {progress.due_at ? (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Due {formatRelative(progress.due_at)}
              </span>
            ) : null}
            {progress.snoozed_until && isSnoozed ? (
              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                <Moon className="h-3 w-3" />
                Snoozed until {formatRelative(progress.snoozed_until)}
              </span>
            ) : null}
          </div>

          {/* Inline form-submit affordance */}
          {inlineAffordance ? (
            <div className="mt-3">
              <Button
                type="button"
                size="sm"
                onClick={() => setDrawerOpen(true)}
              >
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                Open form
              </Button>
            </div>
          ) : null}

          {/* Expanded proof input for note/link/attachment */}
          {(action.kind === "note" ||
            action.kind === "link" ||
            action.kind === "attachment") && (isComplete || expanded) ? (
            <div className="mt-3">
              <TaskProofInput
                progress={progress}
                assignmentId={assignmentId}
              />
            </div>
          ) : null}

          {/* Action row */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {(action.kind === "note" ||
              action.kind === "link" ||
              action.kind === "attachment") &&
            !isComplete ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setExpanded((v) => !v)}
                className="h-7 text-xs"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="mr-1 h-3.5 w-3.5" />
                    Hide proof
                  </>
                ) : (
                  <>
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Add proof
                  </>
                )}
              </Button>
            ) : null}

            {!isComplete && !isSnoozed ? (
              <TaskSnoozeModal
                progressId={progress.id}
                assignmentId={assignmentId}
                taskTitle={progress.task.title}
                trigger={
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                  >
                    <Moon className="mr-1 h-3.5 w-3.5" />
                    Snooze
                  </Button>
                }
              />
            ) : null}

            {isSnoozed ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleUnsnooze}
                disabled={unsnooze.isPending}
                className="h-7 text-xs"
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Unsnooze
              </Button>
            ) : null}

            {isComplete && isSimpleToggle ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => handleSimpleToggle(false)}
                disabled={setStatus.isPending}
                className="h-7 text-xs"
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Mark incomplete
              </Button>
            ) : null}

            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowComments((v) => !v)}
              className="h-7 text-xs"
              aria-expanded={showComments}
            >
              <MessageCircle className="mr-1 h-3.5 w-3.5" />
              {showComments ? "Hide comments" : "Comments"}
            </Button>
          </div>
        </div>

        {expandable ? (
          <div className="hidden md:block pt-0.5">
            {/* placeholder so the right column doesn't drift */}
          </div>
        ) : null}
      </div>

      {showComments ? (
        <div className="px-4 pb-4">
          <TaskCommentThread
            progressId={progress.id}
            expanded={showComments}
          />
        </div>
      ) : null}

      {action.kind === "form_submission" ? (
        <JourneyTaskFormDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          progressId={progress.id}
          formId={action.form_id}
          taskTitle={progress.task.title}
        />
      ) : null}
    </li>
  );
}

function StatusBadge({
  progress,
}: {
  progress: PortalJourneyProgressWithTask;
}) {
  if (progress.status === "completed") {
    return (
      <Badge
        variant="outline"
        className="border-status-active/40 bg-status-active/10 text-[10px] text-status-active"
      >
        Completed
      </Badge>
    );
  }
  if (progress.status === "snoozed") {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 bg-amber-100/60 text-[10px] text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/40 dark:text-amber-200"
      >
        Snoozed
      </Badge>
    );
  }
  if (progress.status === "in_progress") {
    return (
      <Badge variant="outline" className="text-[10px]">
        In progress
      </Badge>
    );
  }
  return null;
}

function ProofKindBadge({
  action,
  isComplete,
}: {
  action: PortalJourneyProgressWithTask["task"]["action"];
  isComplete: boolean;
}) {
  if (action.kind === "attachment") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <Paperclip className="mr-1 h-3 w-3" />
        Attachment {isComplete ? "" : "required"}
      </Badge>
    );
  }
  if (action.kind === "link") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <ExternalLink className="mr-1 h-3 w-3" />
        Link {isComplete ? "" : "required"}
      </Badge>
    );
  }
  if (action.kind === "note") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <Pencil className="mr-1 h-3 w-3" />
        Note {isComplete ? "" : "required"}
      </Badge>
    );
  }
  if (action.kind === "form_submission") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        Form {isComplete ? "submitted" : "submission"}
      </Badge>
    );
  }
  if (action.kind === "info_note") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <Info className="mr-1 h-3 w-3" />
        Read
      </Badge>
    );
  }
  if (action.kind === "info_link") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <ExternalLink className="mr-1 h-3 w-3" />
        Link
      </Badge>
    );
  }
  if (action.kind === "info_attachment") {
    return (
      <Badge variant="secondary" className="font-normal text-[10px]">
        <FileText className="mr-1 h-3 w-3" />
        File
      </Badge>
    );
  }
  return null;
}

// Sub-bucket 14: renders admin-authored payload for info_* kinds inline
// inside the task card body. Partner reads/clicks/downloads and ticks the
// checkbox to complete — no proof payload.
function InfoActionBody({
  action,
}: {
  action: PortalJourneyProgressWithTask["task"]["action"];
}) {
  if (action.kind === "info_note") {
    return (
      <div className="mt-2 whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2 text-xs leading-relaxed text-foreground/90">
        {action.body}
      </div>
    );
  }
  if (action.kind === "info_link") {
    return (
      <div className="mt-2">
        <a
          href={action.href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted/60"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {action.label?.trim() || action.href}
        </a>
      </div>
    );
  }
  if (action.kind === "info_attachment") {
    return <InfoAttachmentDownload assetId={action.asset_id} />;
  }
  return null;
}

// Opens `/api/content/:id/signed-url` on the Hono worker via apiFetch
// (same-origin /api on partners.bookboost.io). Mirrors the
// click-flow in features/portal/resources/resource-card.tsx — reuses
// `useFetchSignedAssetUrl` so the resulting URL is cached for 50 minutes and
// the response shape (`{ url }`) stays in lockstep with that consumer.
function InfoAttachmentDownload({ assetId }: { assetId: string }) {
  const [busy, setBusy] = useState(false);
  const fetchSignedUrl = useFetchSignedAssetUrl();
  async function handleClick() {
    if (busy) return;
    setBusy(true);
    try {
      const { url } = await fetchSignedUrl(assetId);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      // Toasts handled by the surrounding mutation patterns elsewhere; for
      // a one-shot file open we fall back to a console log to avoid pulling
      // sonner into this leaf component.
      // eslint-disable-next-line no-console
      console.error("Failed to load info_attachment signed URL");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted/60 disabled:opacity-60"
      >
        <Download className="h-3.5 w-3.5" />
        {busy ? "Opening…" : "Download file"}
      </button>
    </div>
  );
}
